import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from "axios";

import { keycloak } from "@/utilities/keycloak";
import { useUser } from "@/store/user";
import { useSettings } from "@/store/settings";
import { wait } from "@/utilities/functions/wait";
import { pickBy } from "@/utilities/functions/pickBy";
import { isNotNil } from "@/utilities/functions/isNotNil";
import { CookieStorage } from "@/storage/index";
import { AlertType, useAlert } from "@/store/alert";

const baseUrl = import.meta.env.VITE_API_ENDPOINT;

export const prefix = import.meta.env.VITE_API_PREFIX ?? "";

// const csrfToken = sessionStorage.getItem("csrfToken");

// interface CommonHeaderProperties extends HeadersDefaults {
//   "X-Application": string;
//   "X-FrontendId": string;
//   "X-AppRole": string | number;
//   Authorization: string;
// }
// const getCSRFToken = async () => {
//   return instance.get<{ token: string }>(csrfUrl);
// };

/**
 * Export base config so it can be imported to create instances on the fly.
 * This is useful when BE sends data in varying formats with a data prop that is only part of the entire response.
 */

export const baseConfig = {
  baseURL: baseUrl,
  // withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

/**
 * Export instance so if we need to add glob headers we can access it
 */
export const instance: AxiosInstance = axios.create(baseConfig);

// UPDATE THIS TO MATCH YOUR SETUP
/** Example: Ensure there's a CSRF token before submitting, still do not intercept the CSRF request itself */
// export const onRequestEnsureItHasCSRFToken = async (
//   config: AxiosRequestConfig
// ) => {
//   if (config.url !== csrfUrl) {
//     if (!csrfToken) {
//       const data = (await getCSRFToken()) as unknown as {
//         token: string;
//       };
//       csrfToken = data.token;

//       config.headers.set(
//         "Authorization",
//         `Bearer ${csrfToken}`
//       );
//     }
//   }
//   return config;
// };
/**
 * Some responses will have a "data" prop inside of the "data" prop of axios itself.
 * Extract it so we dont' have to do response.data.data.name
 */
type responseStandard = {
  code: number;
  data: any;
  error: boolean;
  message: string | null;
};
interface responseTypeStandard {
  data: responseStandard;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: AxiosRequestConfig;
  suppressErrorHandling: boolean | null;
}
export const onResponseExtractData = async (
  response: responseTypeStandard
): Promise<responseTypeStandard | responseStandard> => {
  if (response.data?.error == true && (response.data?.code == 401 || response.data?.code == 403)) {
    // 403
    const refreshed = await keycloak.updateToken(99999);
    if (!refreshed) {
      const cookieName = "MangoRedirectPostLogin";
      CookieStorage.setCookie(cookieName, window.location.href, 7);

      const alertStore = useAlert();
      alertStore.fire({
        message: `La tua sessione non è più valida. Effettua il login nuovamente`,
        type: AlertType.Error,
      });
      await wait(3000);
      const userStore = useUser();
      userStore.logout();
    }
  }

  if (response.data?.error == true) {
    return Promise.reject(response.data);
  }

  if (response.data?.data || response.data?.data !== undefined) {
    return response.data;
  }

  if (response.data) {
    return response.data;
  }

  return response;
};

async function err(error: AxiosError) {
  if (error.status == 401) {
    const refreshed = await keycloak.updateToken(60);
    if (!refreshed) {
      // const cookieName = "MangoRedirectPostLogin";
      // CookieStorage.setCookie(cookieName, window.location.href, 7);
      const alertStore = useAlert();
      alertStore.fire({
        message: `La tua sessione non è più valida. Effettua il login nuovamente`,
        type: AlertType.Error,
      });
      await wait(3000);
      // console.log(error);
      const userStore = useUser();
      userStore.logout();
    }
  }
  const suppressErrorHandling = error?.config?.suppressErrorHandling;
  if (suppressErrorHandling) {
    // Restituisci comunque la risposta con un flag speciale
    return Promise.resolve({
      ...error,
      data: {
        ...error?.data,
        errorSuppressed: true,
      },
    });
  }

  return Promise.reject(error);
}

// const onRequestEnsureItHasAppRole = ( config: AxiosRequestConfig ) => {
//   console.table(instance.defaults.headers)
//   return config;
// }
// instance.interceptors.request.use(onRequestEnsureItHasCSRFToken);
// instance.interceptors.request.use(onRequestEnsureItHasAppRole);
instance.interceptors.response.use(onResponseExtractData, err);

export const api = <T>(config: AxiosRequestConfig) => {
  const settingsStore = useSettings();
  const userStore = useUser();
  // if (!Object.prototype.hasOwnProperty.call(instance.defaults.headers, "X-Application")) {
  instance.defaults.headers["X-Application"] = settingsStore.platformIdHeader;
  // }

  if (!Object.prototype.hasOwnProperty.call(instance.defaults.headers, "X-FrontendId")) {
    instance.defaults.headers["X-FrontendId"] = settingsStore.platformFrontendId;
  }

  if (!Object.prototype.hasOwnProperty.call(instance.defaults.headers, "Authorization") && userStore.token) {
    instance.defaults.headers["Authorization"] = `Bearer ${userStore.token ?? ''}`;
  }

  if (
    config?.headers?.["X-AppRole"] === undefined &&
    !Object.prototype.hasOwnProperty.call(instance.defaults.headers, "X-AppRole")
  ) {
    config.headers = {
      ...config.headers,
      ...{
        "X-AppRole": userStore.savedRole?.id ?? userStore.getHigherRoleId,
      },
    };
  }

  return instance.request<any, T>(config);
};

export const generateQueryParamFromObj = (obj: Record<string, string | number | null | boolean>): string => {
  const query: Partial<Record<string, string | number | null>> = pickBy(obj, isNotNil);
  return new URLSearchParams(query).toString();
  // return Object.entries(query)
  //   .map(([key, value]) => `${key}=${value}`)
  //   .join("&");
};
